(() => {
  if (customElements.get('lnvc-inline-video')) return;

  customElements.define('lnvc-inline-video', class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.generation = (this.generation || 0) + 1;
      this.visible = false;
      this.userPaused = false;
      this.expectedPauses = 0;
      this.pending = false;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      this.video.defaultMuted = true;
      this.video.muted = true;
      this.video.addEventListener('pause', () => {
        if (this.expectedPauses) this.expectedPauses -= 1;
        else if (!this.video.ended) this.userPaused = true;
      }, options);
      this.video.addEventListener('play', () => {
        if (!this.pending) this.userPaused = false;
      }, options);
      this.onVisibility = () => {
        if (document.hidden) this.pauseVideo();
        else this.updatePlayback();
      };
      this.onPreference = () => {
        if (this.motion.matches || this.connection?.saveData) this.pauseVideo();
        else this.updatePlayback();
      };
      document.addEventListener('visibilitychange', this.onVisibility, options);
      this.motion.addEventListener('change', this.onPreference);
      this.connection?.addEventListener('change', this.onPreference);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          const entry = entries[entries.length - 1];
          this.visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
          this.updatePlayback();
        }, { threshold: [0, 0.5] });
        this.observer.observe(this.video);
      }
    }

    canAutoplay() {
      return this.isConnected && this.visible && !document.hidden &&
        !this.motion.matches && !this.connection?.saveData &&
        !this.userPaused && !this.video.ended;
    }

    pauseVideo() {
      if (this.video && !this.video.paused) {
        this.expectedPauses += 1;
        this.video.pause();
      }
    }

    async updatePlayback() {
      if (!this.visible || document.hidden) {
        this.pauseVideo();
        return;
      }
      if (!this.canAutoplay() || !this.video.paused || this.pending) return;
      const generation = this.generation;
      this.pending = true;
      this.video.muted = true;
      try {
        await this.video.play();
        if (generation === this.generation && !this.canAutoplay()) this.pauseVideo();
        else if (!this.isConnected) this.video.pause();
      } catch {
        // Browser autoplay restrictions leave the native play control available.
      } finally {
        if (generation === this.generation) this.pending = false;
      }
    }

    disconnectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.observer?.disconnect();
      this.pauseVideo();
      this.events?.abort();
      this.motion?.removeEventListener('change', this.onPreference);
      this.connection?.removeEventListener('change', this.onPreference);
    }
  });
})();
